const TABLE_MANAGED_KEYS = new Set(["page", "page_size", "sort", "sort_dir", "group_by"]);
/** Params that are not table filters but should be preserved across table state updates */
const PRESERVED_KEYS = new Set(["id", "tab"]);
const OP_SUFFIX = "_op";

const DEFAULT_OPERATOR = "contains";

function isOpKey(key) {
  return key.endsWith(OP_SUFFIX);
}

function fieldNameFromOpKey(key) {
  return key.slice(0, -OP_SUFFIX.length);
}

/**
 * Parse table-related query params from a location search string.
 * @returns {{ hasTableState: boolean, page?: number, pageSize?: number, sortInfo?: object, groupBy?: string[], filterValue?: array }}
 */
export function parseTableStateFromSearch(search = "") {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const result = { hasTableState: false };

  if (params.has("page")) {
    const page = parseInt(params.get("page"), 10);
    if (Number.isFinite(page) && page >= 1) {
      result.page = page;
      result.hasTableState = true;
    }
  }

  if (params.has("page_size")) {
    const pageSize = parseInt(params.get("page_size"), 10);
    if (Number.isFinite(pageSize) && pageSize >= 1) {
      result.pageSize = pageSize;
      result.hasTableState = true;
    }
  }

  if (params.has("sort")) {
    const sortName = params.get("sort");
    if (sortName) {
      const dirRaw = params.get("sort_dir");
      const dir = dirRaw === "-1" || dirRaw === "desc" ? -1 : 1;
      result.sortInfo = { id: sortName, name: sortName, dir, type: undefined };
      result.hasTableState = true;
    }
  }

  if (params.has("group_by")) {
    const groupRaw = params.get("group_by");
    if (groupRaw) {
      result.groupBy = groupRaw.split(",").map((g) => g.trim()).filter(Boolean);
      result.hasTableState = true;
    }
  }

  const filterValue = [];
  const seenFields = new Set();

  params.forEach((value, key) => {
    if (TABLE_MANAGED_KEYS.has(key) || PRESERVED_KEYS.has(key) || isOpKey(key)) {
      return;
    }
    if (seenFields.has(key)) {
      return;
    }
    seenFields.add(key);

    const decodedName = decodeURIComponent(key);
    const opKey = `${key}${OP_SUFFIX}`;
    const operator = params.get(opKey) || DEFAULT_OPERATOR;
    const decodedValue = value === null || value === undefined ? "" : String(value);

    if (decodedValue === "") {
      return;
    }

    filterValue.push({
      name: decodedName,
      id: decodedName,
      operator,
      value: decodedValue,
      type: "string"
    });
    result.hasTableState = true;
  });

  if (filterValue.length) {
    result.filterValue = filterValue;
  }

  return result;
}

export function hasTableStateInSearch(search = "") {
  return parseTableStateFromSearch(search).hasTableState;
}

/**
 * Collect query-param keys that represent table filters (value keys and their _op keys).
 * Includes legacy URLs where only the value was set (contains default, no _op param).
 */
function collectFilterParamKeys(search = "") {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const keys = new Set();

  params.forEach((value, key) => {
    if (TABLE_MANAGED_KEYS.has(key) || PRESERVED_KEYS.has(key) || isOpKey(key)) {
      return;
    }
    const hasValue = value !== "" && value !== null && value !== undefined;
    const hasOp = params.has(`${key}${OP_SUFFIX}`);

    if (hasValue || hasOp) {
      keys.add(key);
      keys.add(`${key}${OP_SUFFIX}`);
    }
  });

  params.forEach((_, key) => {
    if (isOpKey(key)) {
      keys.add(key);
      keys.add(fieldNameFromOpKey(key));
    }
  });

  return keys;
}

/**
 * Build URLSearchParams for table state, preserving unrelated query params.
 */
export function buildTableSearchParams(pagination, existingSearch = "") {
  const existing = new URLSearchParams(existingSearch.startsWith("?") ? existingSearch.slice(1) : existingSearch);
  const next = new URLSearchParams();
  const filterParamKeys = collectFilterParamKeys(existingSearch);

  existing.forEach((value, key) => {
    if (TABLE_MANAGED_KEYS.has(key) || isOpKey(key) || filterParamKeys.has(key)) {
      return;
    }
    next.set(key, value);
  });

  if (!pagination) {
    return next;
  }

  const { skip = 0, limit = 10, sortInfo, groupBy, filterValue } = pagination;
  const page = Math.floor(skip / limit) + 1;

  if (page > 1) {
    next.set("page", String(page));
  }

  if (limit && limit !== 10) {
    next.set("page_size", String(limit));
  }

  if (sortInfo?.name) {
    next.set("sort", sortInfo.name);
    next.set("sort_dir", String(sortInfo.dir ?? 1));
  }

  if (groupBy?.length) {
    next.set("group_by", groupBy.join(","));
  }

  const activeFilters = (filterValue || []).filter(
    ({ value }) => value !== "" && value !== null && value !== undefined
  );

  activeFilters.forEach(({ name, operator, value }) => {
    const serializedValue = Array.isArray(value) ? value.join("---") : String(value);
    next.set(name, serializedValue);
    if (operator) {
      next.set(`${name}${OP_SUFFIX}`, operator);
    }
  });

  return next;
}

export function paginationFromTableSearch(search, basePagination = {}) {
  const parsed = parseTableStateFromSearch(search);
  if (!parsed.hasTableState) {
    return basePagination;
  }

  const limit = parsed.pageSize ?? basePagination.limit ?? 10;
  const page = parsed.page ?? 1;
  const skip = (page - 1) * limit;

  return {
    ...basePagination,
    skip,
    limit,
    ...(parsed.sortInfo && { sortInfo: parsed.sortInfo }),
    ...(parsed.groupBy && { groupBy: parsed.groupBy }),
    ...(parsed.filterValue && { filterValue: parsed.filterValue })
  };
}

export function searchFromPagination(pagination, existingSearch = "") {
  const params = buildTableSearchParams(pagination, existingSearch);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const LIST_RETURN_SEARCH_STATE_KEY = "listReturnSearch";

/**
 * Build a react-router location for navigating from a list row to a detail page.
 * Preserves list query params (page, filters, etc.) for the back button.
 */
export function getListDetailLocation(pathName, id, listSearch = "", { view = false } = {}) {
  return {
    pathname: view ? `${pathName}/${id}/view` : `${pathName}/${id}`,
    state: { [LIST_RETURN_SEARCH_STATE_KEY]: listSearch }
  };
}

/**
 * Build a react-router location for returning to the list with preserved query params.
 */
export function getListReturnLocation(pathName, location) {
  const savedSearch = location?.state?.[LIST_RETURN_SEARCH_STATE_KEY];
  return {
    pathname: pathName,
    search: savedSearch ?? ""
  };
}
